import { useState, useEffect, FocusEvent, FormEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useTranslator from '../useTranslator';
import usePrevious from '../usePrevious';
import { ValidationErrors } from '../../redux/user/types';
import { Dictionary } from '../../../../types/globals';
import { apiIsFacebookOnlyUser } from '../../redux/user/facebook/actions';
import StateInterface from '../../redux/types';
import useConfig from '../useConfig';
import { FabricSampleAddressFields } from '../../redux/productdetail/fabricSample';
import registrationFormValidator, { RegistrationFormValidator } from './registrationFormValidator';
import loginFormValidator, { LoginFormValidator } from './loginFormValidator';
import isFacebookOnlyUserValidator from './isFacebookOnlyUserValidator';

type FormErrors = LoginFormValidator & RegistrationFormValidator & FabricSampleAddressFields;
type Validators = typeof loginFormValidator | typeof registrationFormValidator;
type FormElementProps = { type: string; name: string; value: string | boolean; checked?: boolean };
type InputEntryGetter = (element: FormElementProps) => [string, string | boolean | undefined];

interface ChangeProps {
    target: FormElementProps;
}

export const normalizeValidationErrors = (validationErrors: ValidationErrors) => {
    return Object.entries(validationErrors)
        .map(([key, error]) => ({
            [key]: Array.isArray(error) ? error[0] || '' : error,
        }))
        .reduce(
            (acc, errorObj) => ({
                ...acc,
                ...errorObj,
            }),
            {}
        );
};

const useForm = <T extends Dictionary<any>>(
    callback: Function,
    validator: Validators,
    validationErrors: ValidationErrors = {},
    initialFormValues: T
    // eslint-disable-next-line max-params
) => {
    const [values, setValues] = useState(initialFormValues);
    const [errors, setErrors] = useState({} as FormErrors);
    const dispatch = useDispatch();
    const t = useTranslator();
    const config = useConfig();
    const { isFacebookOnlyUser, isFetching } = useSelector<
        StateInterface,
        { isFacebookOnlyUser: boolean; isFetching: boolean }
    >(state => state.isFacebookOnlyUser);
    const didFetch = usePrevious(isFetching) && !isFetching;
    useEffect(() => {
        if (Object.keys(validationErrors).length > 0) {
            setErrors(normalizeValidationErrors(validationErrors) as FormErrors);
        }
    }, [validationErrors]);

    useEffect(() => {
        if (didFetch) {
            const { password: passwordError } = isFacebookOnlyUserValidator({
                email: values.email,
                isFacebookOnlyUser,
                forgotPasswordLink: config.links.forgotPassword,
                t,
            });
            const errorHolder = { ...errors };
            if (passwordError) {
                errorHolder.password = passwordError;
            } else {
                delete errorHolder.password;
            }
            setErrors(errorHolder);
        }
    }, [config.links.forgotPassword, isFacebookOnlyUser, isFetching, values.email, t, didFetch, errors]);

    const getInputEntry: InputEntryGetter = element => [
        element.name,
        element.type === 'checkbox' ? element.checked : element.value,
    ];

    const handleValueChange = (name: string, value: string | boolean | undefined) => {
        setValues({
            ...values,
            [name]: value,
        });

        const errorHolder = { ...errors };
        if (errorHolder[name]) {
            delete errorHolder[name];
            setErrors(errorHolder);
        }
    };

    const onChange = (event: ChangeProps) => {
        const { target } = event;
        const [name, value] = getInputEntry(target);
        handleValueChange(name, value);
    };

    // This is mainly needed for text inputs where the browser may make suggestions
    // Inconsistently (Current scenario is email and password suggestion on login/register overlay),
    // browsers do not call onChange when the user clicks the suggestion, but onBlur gets called
    const onBlur = (event: FocusEvent<HTMLInputElement>) => {
        const { target } = event;
        const [name, value] = getInputEntry(target);

        if (value && value !== values[name]) {
            handleValueChange(name, value);
        }
    };

    const onBlurValidateFacebookOnly = (event: FocusEvent<HTMLInputElement>) => {
        onBlur(event);
        if (values.email.length > 0) {
            dispatch(apiIsFacebookOnlyUser(values.email));
        }
    };

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const err = validator(values);
        if (Object.keys(err).length === 0) {
            callback(values);
        } else {
            setErrors(err);
        }
    };

    const resetFormValue = (values: T) => {
        setValues(values);
    };

    return {
        values,
        errors,
        onChange,
        onBlur,
        onBlurValidateFacebookOnly,
        onSubmit,
        resetFormValue,
    };
};

export type BellaForm = ReturnType<typeof useForm>;

export default useForm;
